import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '@/config/firebase'
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { useAuthStore } from '@/store/authStore'
import toast from 'react-hot-toast'
import { Hash, Search } from 'lucide-react'

export default function JoinOrgPage() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const [code, setCode] = useState('')
    const [loading, setLoading] = useState(false)

    const handleJoin = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user || !code.trim()) return

        setLoading(true)
        try {
            // 1. Buscar org pelo orgCode
            const q = query(collection(db, 'organizations'), where('orgCode', '==', code.trim().toUpperCase()))
            const querySnapshot = await getDocs(q)

            if (querySnapshot.empty) {
                toast.error('Código da organização não encontrado.')
                setLoading(false)
                return
            }

            const orgDoc = querySnapshot.docs[0]
            const orgId = orgDoc.id

            // 2. Adicionar usuário como membro na subcoleção
            const memberSubRef = doc(db, `organizations/${orgId}/members`, user.uid)
            await setDoc(memberSubRef, {
                uid: user.uid,
                role: 'member',
                joinedAt: serverTimestamp(),
            })

            // 3. Adicionar no root members para busca global do App.tsx
            const memberRootRef = doc(db, 'members', `${user.uid}_${orgId}`)
            await setDoc(memberRootRef, {
                userId: user.uid,
                orgId: orgId,
                role: 'member',
                joinedAt: serverTimestamp(),
            })

            toast.success('Você entrou na organização!')

            // Forçar recarregamento do App para pegar a norg
            window.location.href = '/dashboard'
        } catch (error: any) {
            console.error('Erro ao entrar na organização:', error)
            toast.error('Erro ao entrar na organização: ' + error.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="h-screen bg-white flex flex-col justify-center overflow-hidden font-sans relative">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#d4a94d]/5 rounded-full blur-[100px] -mr-40 -mt-40 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#001c28]/5 rounded-full blur-[100px] -ml-40 -mb-40 pointer-events-none"></div>

            <div className="max-w-md mx-auto w-full px-4 h-full flex flex-col justify-center animate-in fade-in zoom-in duration-700 relative z-10">
                <div className="text-center mb-6 shrink-0">
                    <div className="w-40 h-40 mx-auto mb-2">
                        <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
                    </div>
                    <h2 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-tight">
                        Vincular Organização
                    </h2>
                    <p className="text-[#001c28] font-bold uppercase tracking-widest text-xs mt-1">
                        Digite o código para entrar
                    </p>
                </div>

                <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-[0_15px_50px_-12px_rgba(0,28,40,0.15)] border-2 border-[#001c28]/5 relative overflow-hidden shrink-0">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#d4a94d]/10 rounded-bl-[3rem] -mr-12 -mt-12 pointer-events-none"></div>

                    <form className="space-y-5" onSubmit={handleJoin}>
                        <div className="space-y-2">
                            <label htmlFor="code" className="text-[10px] font-black text-[#001c28] uppercase tracking-widest ml-1">
                                Código da Organização
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                    <Hash className="h-5 w-5 text-[#001c28]/40 group-focus-within:text-[#d4a94d] transition-colors" />
                                </div>
                                <input
                                    id="code"
                                    type="text"
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="EX: A1B2C3"
                                    className="w-full bg-[#001c28]/[0.02] border-2 border-[#001c28]/10 focus:bg-white focus:border-[#d4a94d] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold uppercase tracking-widest text-[#001c28] outline-none transition-all placeholder:text-[#001c28]/20"
                                />
                            </div>
                        </div>

                        <div className="bg-[#001c28]/5 p-5 rounded-2xl border border-[#001c28]/10 text-center">
                            <p className="text-[10px] text-[#001c28] font-bold uppercase tracking-widest leading-relaxed">
                                Peça o código de 6 dígitos para o administrador da sua organização.
                            </p>
                        </div>

                        <div className="pt-2 space-y-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center items-center space-x-2 py-4 rounded-xl text-xs font-black uppercase tracking-widest text-white bg-[#d4a94d] hover:bg-[#001c28] shadow-lg shadow-[#d4a94d]/30 hover:shadow-[#001c28]/30 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        <span>Entrar na Organização</span>
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/create-org')}
                                className="w-full text-center text-[10px] font-black text-[#001c28]/40 hover:text-[#001c28] uppercase tracking-widest transition-colors"
                            >
                                Ou criar minha própria organização
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
